import React, {useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import CodeTextarea from "../components/CodeTextarea";
import {runJava, transpileJava} from "../lib/java-transpiler";

const SAMPLE = `import java.util.Scanner;

public class Main {
    public static int factorial(int n) {
        if (n <= 1) {
            return 1;
        }
        return n * factorial(n - 1);
    }

    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        int a = scanner.nextInt();
        int b = scanner.nextInt();
        System.out.println(a + b);
        System.out.println(a / b);

        double d = 5;
        System.out.println(d / 2);

        for (int i = 0; i < 5; i++) {
            System.out.println("factorial(" + i + ") = " + factorial(i));
        }

        boolean flag = a > b && b > 0;
        System.out.println(flag);
    }
}
`;

const JavaPlaygroundTestPage: React.FC = () => {
    const [code, setCode] = useState(SAMPLE);
    const [stdin, setStdin] = useState("3 4\n");
    const [output, setOutput] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [generatedJs, setGeneratedJs] = useState<string | null>(null);
    const [showJs, setShowJs] = useState(false);

    const handleRun = () => {
        setErrorMsg(null);
        setOutput(null);

        const transpiled = transpileJava(code);
        setGeneratedJs(transpiled.js);

        const result = runJava(code, {maxSteps: 200_000, stdin});
        setOutput(result.output);
        if (!result.ok && result.error) {
            setErrorMsg(`${result.error.type}: ${result.error.message}`);
        }
    };

    return (
        <Container sx={{py: 4}}>
            <Typography sx={{fontSize: 26, fontWeight: 800, mb: 1}}>
                Java Playground (experimental, no backend)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                Transpiles a small subset of Java to JS and runs it entirely in the browser.
                Nothing here is graded or sent to any API — it's a sandbox for testing the
                transpiler itself.
            </Typography>

            <TextField
                label="Standard input (stdin)"
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                sx={{mb: 2}}
                helperText="Fed to Scanner(System.in). Whitespace-separated tokens for nextInt()/next(), newlines for nextLine()."
            />

            <Paper elevation={0} sx={(theme) => ({border: `1px solid ${theme.palette.divider}`, mb: 2})}>
                <CodeTextarea height={420} language="java" value={code} onChange={setCode}/>
                <Box
                    sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    })}
                >
                    <Button size="small" onClick={() => setShowJs((v) => !v)}>
                        {showJs ? "Hide generated JS" : "Show generated JS"}
                    </Button>
                    <Button variant="contained" onClick={handleRun}>
                        Run
                    </Button>
                </Box>
            </Paper>

            {showJs && generatedJs && (
                <Paper
                    elevation={0}
                    component="pre"
                    sx={(theme) => ({
                        p: 2,
                        mb: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: "auto",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        fontSize: 13,
                    })}
                >
                    {generatedJs}
                </Paper>
            )}

            {errorMsg && (
                <Alert severity="error" sx={{mb: 2}}>
                    {errorMsg}
                </Alert>
            )}

            {output !== null && (
                <Paper elevation={0} sx={(theme) => ({p: 2, border: `1px solid ${theme.palette.divider}`})}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2">Output</Typography>
                        <Chip
                            label={errorMsg ? "Error" : "OK"}
                            color={errorMsg ? "error" : "success"}
                            size="small"
                        />
                    </Box>
                    <Divider sx={{mb: 1.5}}/>
                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: 13,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {output || "(no output)"}
                    </Box>
                </Paper>
            )}
        </Container>
    );
};

export default JavaPlaygroundTestPage;
